/**
 * Mix Recipe Picker — QC's 3-option decision screen.
 * ─────────────────────────────────────────────────────────────────────────────
 * Where in the workflow this lives:
 *   1. QC opens this page, picks count + yarn_type for the order.
 *   2. System resolves 3 sibling recipes (LOWEST_SAFE / BALANCED / PREMIUM)
 *      with predicted raw cost, effective cost/kg yarn, realization, waste,
 *      cost tier, approval chain, and Sec 10 hard-blocking warnings.
 *   3. QC reviews side-by-side and picks one.
 *   4. The picked option flows downstream to Laydown Planning (next sidebar
 *      entry) where bales get matched against it.
 *   5. Mixing Plan page captures the GM/MD approval per Sec 9 chain.
 *   6. Laydown Execution runs the mix.
 *
 * The Cotton Grades and Mixing Recipes master editors remain under
 * Bale Management → Masters — this page is the *daily-use picker*, not the
 * masters CRUD.
 */
import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Empty, InputNumber, Row, Select, Space, Spin,
  Statistic, Table, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, StopOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

interface RecipeLine { grade_code: string; pct: string }
interface CostBreakdown {
  grade_code: string
  pct: string
  landed_cost_inr_per_kg: string | null
  landed_cost_source: string
  contribution_inr_per_kg: string | null
}

// Sprint 0071 — stock simulation + Uster verdict types.
interface UsterCheck {
  check_id: string
  label: string
  value: number | null
  limit: number | null
  unit: string
  ok: boolean
  severity: 'HARD' | 'WARN'
  category: 'WITHIN' | 'DRIFT' | 'FEASIBILITY'
}
interface VarianceStat {
  mean?: number | null
  sd?: number | null
  cv_pct?: number | null
  min?: number | null
  max?: number | null
  n?: number | null
  total_weight?: number | null
}
interface StockSimulation {
  variant: string
  feasible: boolean
  picked_bale_count: number
  picked_total_kg: number
  shortfall_by_grade: Record<string, number>
  avg_landed_cost_inr_per_kg: number | null
  within_variance: Record<string, VarianceStat>
  length_range_mm: number | null
  drift_basis: { prev_laydown_id: string | null; approved_at: string | null; blowroom_line_id: string | null }
  uster_checks: UsterCheck[]
  // Sprint 0071 — closeness score 0..100. Higher = closer to benchmark.
  benchmark_score: number | null
  verdict: 'ON_BENCHMARK' | 'NEAR_BENCHMARK' | 'OFF_BENCHMARK' | 'FAR_OFF' | 'INFEASIBLE'
  major_reasons: string[]
  minor_reasons: string[]
  // Legacy fields kept for fallback rendering during transition
  block_reasons: string[]
  warn_reasons: string[]
  simulated_at: string
}

interface RecipeOption {
  id: string
  recipe_variant: string
  cost_tier: string | null
  status: string
  recipe_label: string | null
  approval_level: string | null
  lines: RecipeLine[]
  cost_breakdown: CostBreakdown[]
  raw_cost_inr_per_kg: string | null
  expected_waste_pct: string | null
  expected_realization_pct: string | null
  effective_cost_inr_per_kg_yarn: string | null
  risk_band: string
  notes: string[]
  stock_simulation: StockSimulation | null
  benchmark_recommended: boolean
}
interface OptionsResponse {
  count_ne: string
  yarn_type: string
  plant_id: string
  baseline_b_cost_inr_per_kg: string
  stock_simulation_target_bales: number | null
  options: RecipeOption[]
}

// Sprint 0071 — verdict bands map a 0..100 closeness score to a UI
// colour. Nothing is BLOCKED — even FAR_OFF stays selectable so the
// picker reflects today's actual stock reality.
const VERDICT_BADGE: Record<string, { color: string; bg: string; border: string; label: string; icon: React.ReactNode }> = {
  ON_BENCHMARK:   { color: 'green',   bg: '#f6ffed', border: '#b7eb8f',
                    label: '✓ On benchmark',           icon: <CheckCircleOutlined /> },
  NEAR_BENCHMARK: { color: 'lime',    bg: '#fcffe6', border: '#eaff8f',
                    label: '◐ Near benchmark',          icon: <CheckCircleOutlined /> },
  OFF_BENCHMARK:  { color: 'orange',  bg: '#fffbe6', border: '#ffe58f',
                    label: '⚠ Off benchmark',          icon: <WarningOutlined /> },
  FAR_OFF:        { color: 'red',     bg: '#fff1f0', border: '#ffccc7',
                    label: '✕ Far from benchmark',     icon: <WarningOutlined /> },
  INFEASIBLE:     { color: 'volcano', bg: '#fff2e8', border: '#ffbb96',
                    label: '⛔ Insufficient stock',    icon: <StopOutlined /> },
}

/** One pill in the Uster gate row — red if violated, green if within. */
function UsterGatePill({ check }: { check: UsterCheck }) {
  const color = check.ok ? 'green' : check.severity === 'HARD' ? 'red' : 'orange'
  // Compact label: "Mic CV 7.4% (≤8%)" or "Δ Mic 0.08 (≤0.1)"
  const display = check.value !== null && check.value !== undefined
    ? `${check.value}${check.unit}`
    : '—'
  const limit = check.limit !== null && check.limit !== undefined
    ? `≤${check.limit}${check.unit}`
    : ''
  // Strip leading "Within" / "Δ" prefixes for short pill text
  const short = check.label
    .replace(/^Δ /, 'Δ ')
    .replace(/ within laydown$/, '')
    .replace(/ vs last laydown$/, '')
    .replace(/^Mean /, '')
  return (
    <Tag
      color={color}
      style={{ fontSize: 10, margin: '2px 2px 0 0', padding: '0 6px' }}
      title={check.label + (limit ? ` ${limit}` : '')}
    >
      {short}: <b>{display}</b> {limit && <span style={{ opacity: 0.7 }}>{limit}</span>}
    </Tag>
  )
}

/** Tiny horizontal score bar — shows where the variant sits on the
 *  0..100 benchmark-closeness scale. Colour ramps red→amber→green. */
function ScoreBar({ score }: { score: number | null }) {
  if (score === null || score === undefined) {
    return <Text style={{ fontSize: 11, color: '#999' }}>Score n/a</Text>
  }
  // Smooth red→green ramp via HSL hue 0..120
  const hue = Math.max(0, Math.min(120, score * 1.2))
  const barColor = `hsl(${hue}, 70%, 45%)`
  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 10, color: '#666', marginBottom: 2,
      }}>
        <span>Closeness to Uster benchmark</span>
        <span style={{ fontWeight: 700, color: barColor, fontVariantNumeric: 'tabular-nums' }}>
          {score.toFixed(0)} / 100
        </span>
      </div>
      <div style={{
        background: '#f0f0f0', height: 8, borderRadius: 4,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          width: `${Math.max(2, Math.min(100, score))}%`,
          height: '100%', background: barColor,
          borderRadius: 4, transition: 'width 0.3s, background 0.3s',
        }} />
      </div>
    </div>
  )
}

/** "Stock Reality" panel — shown inside each option card when the
 *  backend returned a stock simulation. This is the source of truth for
 *  the picker's decision (cost is from real picked bales, benchmark
 *  closeness scored). The forecast block above stays as a quoting
 *  reference. Sprint 0071: nothing is blocked here — the panel
 *  visualises closeness so the user picks the best variant for
 *  TODAY'S stock, not yesterday's hypothetical. */
function StockRealityPanel({ sim }: { sim: StockSimulation }) {
  const verdictCfg = VERDICT_BADGE[sim.verdict] ?? VERDICT_BADGE.NEAR_BENCHMARK
  const withinChecks = sim.uster_checks.filter(c => c.category === 'WITHIN')
  const driftChecks = sim.uster_checks.filter(c => c.category === 'DRIFT')
  const hasDriftBasis = !!sim.drift_basis?.prev_laydown_id
  const shortfallEntries = Object.entries(sim.shortfall_by_grade ?? {})

  return (
    <div
      style={{
        background: verdictCfg.bg,
        border: `1px solid ${verdictCfg.border}`,
        borderRadius: 6,
        padding: '8px 10px',
        marginTop: 4,
      }}
    >
      <Space size={4} wrap style={{ marginBottom: 6 }}>
        <Tag color={verdictCfg.color} style={{ fontWeight: 700, margin: 0 }}>
          {verdictCfg.icon} {verdictCfg.label}
        </Tag>
      </Space>

      <div style={{ marginBottom: 8 }}>
        <ScoreBar score={sim.benchmark_score} />
      </div>

      <Row gutter={8} style={{ marginBottom: 6 }}>
        <Col span={12}>
          <div style={{ fontSize: 10, color: '#666' }}>Avg Landed Cost</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0958d9', fontVariantNumeric: 'tabular-nums' }}>
            {sim.avg_landed_cost_inr_per_kg !== null && sim.avg_landed_cost_inr_per_kg !== undefined
              ? `₹${sim.avg_landed_cost_inr_per_kg.toFixed(2)}/kg`
              : '—'}
          </div>
          <div style={{ fontSize: 10, color: '#888' }}>
            {sim.picked_bale_count} bales · {sim.picked_total_kg.toLocaleString('en-IN')} kg
          </div>
        </Col>
        <Col span={12}>
          <div style={{ fontSize: 10, color: '#666' }}>UHML Range</div>
          <div style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {sim.length_range_mm !== null && sim.length_range_mm !== undefined
              ? `${sim.length_range_mm.toFixed(2)} mm`
              : '—'}
          </div>
          <div style={{ fontSize: 10, color: '#888' }}>target ≤ 2.0 mm</div>
        </Col>
      </Row>

      {shortfallEntries.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 11, marginBottom: 6, padding: '4px 8px' }}
          message={
            <span>
              <b>Stock short:</b>{' '}
              {shortfallEntries.map(([g, n]) => `${g} short ${n}`).join(', ')}.
              Picker filled what's available — top up godown or accept smaller laydown.
            </span>
          }
        />
      )}

      {withinChecks.length > 0 && (
        <div style={{ marginBottom: 4 }}>
          <Text style={{ fontSize: 10, color: '#666' }}>
            Within-Laydown (target §A)
          </Text>
          <div>{withinChecks.map(c => <UsterGatePill key={c.check_id} check={c} />)}</div>
        </div>
      )}

      {driftChecks.length > 0 && (
        <div style={{ marginBottom: 4 }}>
          <Text style={{ fontSize: 10, color: '#666' }}>
            Drift vs prior laydown (target §B)
          </Text>
          <div>{driftChecks.map(c => <UsterGatePill key={c.check_id} check={c} />)}</div>
        </div>
      )}

      {!hasDriftBasis && (
        <Text style={{ fontSize: 10, color: '#999' }}>
          No prior approved laydown for this plant — drift comparison skipped.
        </Text>
      )}
    </div>
  )
}

const YARN_TYPES = ['CARDED_COMPACT', 'COMPACT', 'SIRO', 'COMBED', 'COMBED_COMPACT']
const COUNTS = [8, 10, 12, 16, 20, 24, 30, 32, 34, 40, 45, 50, 60, 70, 80, 90, 100, 110, 120]

const STATUS_TAG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  ALLOWED:          { color: 'green',   icon: <CheckCircleOutlined />, label: 'Allowed' },
  SPECIAL_APPROVAL: { color: 'orange',  icon: <ClockCircleOutlined />, label: 'Special Approval' },
  NOT_RECOMMENDED:  { color: 'volcano', icon: <WarningOutlined />,     label: 'Not Recommended' },
  BLOCKED:          { color: 'red',     icon: <StopOutlined />,        label: 'Blocked' },
}
const VARIANT_TAG: Record<string, { color: string; label: string }> = {
  LOWEST_SAFE: { color: 'gold',   label: 'Lowest Safe Cost' },
  BALANCED:    { color: 'blue',   label: 'Balanced (Recommended)' },
  PREMIUM:     { color: 'purple', label: 'Premium' },
}
const RISK_TAG: Record<string, { color: string; label: string }> = {
  LOW:     { color: 'green',   label: 'Low Risk' },
  MEDIUM:  { color: 'orange',  label: 'Medium Risk' },
  HIGH:    { color: 'volcano', label: 'High Risk' },
  BLOCKED: { color: 'red',     label: 'Blocked' },
}

export default function MixRecipePickerPage() {
  const { plantId } = usePlantId()
  const [countNe, setCountNe] = useState<number>(30)
  const [yarnType, setYarnType] = useState<string>('COMBED')
  const [baselineB, setBaselineB] = useState<number>(68)
  // Sprint 0071 — bales to simulate against current stock. 40 = typical
  // mid-size laydown for a 3-4 ring frame Indian mill. Operator can flex
  // up/down to size for their actual SO.
  const [targetBales, setTargetBales] = useState<number>(40)
  // Phase 5 — track the user's selection so they can confirm one option
  // and feed it forward (Mix Plan flow consumes recipe_variant downstream)
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<OptionsResponse>({
    queryKey: ['recipe-options', plantId, countNe, yarnType, baselineB, targetBales],
    queryFn: () => apiClient.get('/recipes/options', {
      params: {
        plant_id: plantId, count_ne: countNe,
        yarn_type: yarnType, baseline_b_cost: baselineB,
        // Sprint 0071 — request the live stock simulation + Uster
        // verdict for each variant. Without this the picker is
        // forecast-only and can mislead.
        with_stock_simulation: true,
        target_bales: targetBales,
      },
    }).then(r => r.data),
    enabled: !!plantId,
    retry: false,
  })

  const errorMsg = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail

  return (
    <App>
      <PageHeader
        title="Mix Recipe Picker"
        subtitle="QC step 1 — pick count + yarn type, system shows 3 sibling recipes with predicted cost, realization, and risk for side-by-side comparison"
      />

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Count (Ne)</Text>
            <Select
              style={{ width: 120 }}
              value={countNe}
              onChange={setCountNe}
              options={COUNTS.map(c => ({ value: c, label: `${c}s` }))}
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Yarn Type</Text>
            <Select
              style={{ width: 200 }}
              value={yarnType}
              onChange={setYarnType}
              options={YARN_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))}
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              Base B-grade ₹/kg <span title="Used as the cost-index fallback when there is no inventory data for a grade">ⓘ</span>
            </Text>
            <InputNumber
              style={{ width: 130 }}
              value={baselineB}
              onChange={v => setBaselineB(v ?? 68)}
              min={1}
              addonBefore="₹"
              addonAfter="/kg"
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              Target bales <span title="Laydown size for the stock simulation. Picker fetches FIFO from current stock + applies Uster gates against this many bales.">ⓘ</span>
            </Text>
            <InputNumber
              style={{ width: 110 }}
              value={targetBales}
              onChange={v => setTargetBales(v ?? 40)}
              min={10}
              max={200}
              step={5}
              addonAfter="bales"
            />
          </div>
        </Space>
      </Card>

      {/* Sprint 0071 — picker philosophy banner. The Uster benchmarks
          are the TARGET, not a wall. We always mix from what stock
          allows; the picker scores how close each variant gets and
          recommends the closest one. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="How this picker works"
        description={
          <span style={{ fontSize: 12 }}>
            Each variant runs a real bale pick against your <b>current godown stock</b> — bales are chosen
            for closeness to the grade pool's median Mic / UHML to <b>minimise within-laydown CV</b>. Each
            variant gets a <b>0–100 closeness score</b> against the Cotton USA / Uster benchmarks
            (Mic CV ≤ 8%, Rd CV ≤ 5%, +b CV ≤ 8%, UHML range ≤ 2 mm,
            Δ Mic ≤ 0.1 vs prior laydown, Δ +b ≤ 0.2, Δ length ≤ 0.5 mm).
            The variant <b>closest to benchmark</b> is tagged <b>BEST FIT FOR TODAY'S STOCK</b>; cheaper
            variants stay selectable so you can override when commercial reasons demand it.
          </span>
        }
      />

      {!plantId && <Empty description="Select a plant first" />}

      {isLoading && <Spin tip="Resolving 3 options + cost engine…"><div style={{ height: 200 }} /></Spin>}

      {errorMsg && <Alert type="warning" showIcon message={errorMsg} />}

      {data && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Space wrap>
                <span>3 options for <b>{countNe}s {yarnType.replace(/_/g, ' ')}</b></span>
                <Text type="secondary">·</Text>
                <span>Base B-grade ref: ₹{baselineB}/kg</span>
                <Text type="secondary">·</Text>
                <span>Costs from inventory weighted-avg, fallback cost-index × base</span>
              </Space>
            }
          />

          <Row gutter={16}>
            {data.options.map(opt => {
              const variantCfg = VARIANT_TAG[opt.recipe_variant] ?? { color: 'default', label: opt.recipe_variant }
              const statusCfg = STATUS_TAG[opt.status] ?? { color: 'default', label: opt.status, icon: null }
              const riskCfg = RISK_TAG[opt.risk_band] ?? { color: 'default', label: opt.risk_band }
              const isBalanced = opt.recipe_variant === 'BALANCED'
              // Sprint 0071 — BEST FIT is determined by closeness to
              // benchmark (backend), not by hardcoded variant name.
              const isBestFit = opt.benchmark_recommended
              return (
                <Col xs={24} md={8} key={opt.id} style={{ marginBottom: 16 }}>
                  <Card
                    size="small"
                    style={{
                      borderTop: `4px solid var(--${variantCfg.color})`,
                      ...(isBestFit
                        ? { boxShadow: '0 0 0 2px #52c41a', borderLeft: '3px solid #52c41a' }
                        : isBalanced ? { borderLeft: '2px solid #1677ff' } : {}),
                      height: '100%',
                    }}
                    title={
                      <Space wrap size="small">
                        <Tag color={variantCfg.color}>{variantCfg.label}</Tag>
                        {isBestFit && (
                          <Tag color="green" style={{ fontWeight: 700 }}>
                            ★ BEST FIT FOR TODAY&apos;S STOCK
                          </Tag>
                        )}
                        {!isBestFit && isBalanced && <Tag color="blue">DEFAULT</Tag>}
                      </Space>
                    }
                    extra={<Tag color={riskCfg.color}>{riskCfg.label}</Tag>}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                      <div>
                        <Tag icon={statusCfg.icon} color={statusCfg.color}>{statusCfg.label}</Tag>
                        <Text code style={{ fontSize: 13, marginLeft: 4 }}>
                          {opt.recipe_label || '—'}
                        </Text>
                      </div>

                      {/* Forecast cost block — kept as a quoting
                          reference but tinted to make clear the live
                          Stock Reality panel below is the primary
                          source for picker decisions. */}
                      <div style={{
                        background: '#fafafa', padding: '6px 8px',
                        borderRadius: 4, border: '1px dashed #d9d9d9',
                      }}>
                        <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                          Forecast — cost-index estimate
                        </div>
                        <Row gutter={8}>
                          <Col span={12}>
                            <Statistic
                              title={<span style={{ fontSize: 11 }}>Raw Cotton</span>}
                              value={opt.raw_cost_inr_per_kg ?? '—'}
                              prefix="₹"
                              suffix="/kg"
                              precision={2}
                              valueStyle={{ fontSize: 14, color: '#666' }}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title={<span style={{ fontSize: 11 }}>Per kg Yarn</span>}
                              value={opt.effective_cost_inr_per_kg_yarn ?? '—'}
                              prefix="₹"
                              precision={2}
                              valueStyle={{ fontSize: 14, color: '#666' }}
                            />
                          </Col>
                        </Row>
                      </div>

                      <Row gutter={8}>
                        <Col span={12}>
                          <Text style={{ fontSize: 11 }}>
                            Realization: <b>{opt.expected_realization_pct ?? '—'}%</b>
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Text style={{ fontSize: 11 }}>
                            Waste: <b>{opt.expected_waste_pct ?? '—'}%</b>
                          </Text>
                        </Col>
                      </Row>

                      <Space size={4} wrap>
                        {opt.cost_tier && (
                          <Tag style={{ fontSize: 10 }}>{opt.cost_tier.replace(/_/g, ' ')}</Tag>
                        )}
                        {opt.approval_level && (
                          <Tag color="geekblue" style={{ fontSize: 10 }}>
                            Approval: {opt.approval_level.replace(/_/g, '+')}
                          </Tag>
                        )}
                      </Space>

                      <div>
                        <Text style={{ fontSize: 11 }} type="secondary">
                          Grade Mix → Cost
                        </Text>
                        <Table
                          size="small"
                          rowKey="grade_code"
                          dataSource={opt.cost_breakdown}
                          pagination={false}
                          showHeader={false}
                          style={{ fontSize: 11 }}
                          columns={[
                            { title: 'G', dataIndex: 'grade_code', width: 35,
                              render: (v: string) => <Tag style={{ fontSize: 10, margin: 0 }}>{v}</Tag> },
                            { title: '%', dataIndex: 'pct', width: 50, align: 'right',
                              render: (v: string) => <span>{Number(v).toFixed(0)}%</span> },
                            { title: '₹/kg', dataIndex: 'landed_cost_inr_per_kg', width: 60, align: 'right',
                              render: (v: string | null, r: CostBreakdown) =>
                                v ? <span title={`Source: ${r.landed_cost_source}`}>₹{Number(v).toFixed(2)}</span>
                                  : <Text type="secondary">—</Text> },
                            { title: 'cont', dataIndex: 'contribution_inr_per_kg', width: 60, align: 'right',
                              render: (v: string | null) =>
                                v ? <Text style={{ fontSize: 11 }}><b>₹{Number(v).toFixed(2)}</b></Text>
                                  : <Text type="secondary">—</Text> },
                          ]}
                        />
                      </div>

                      {/* Sprint 0071 — Stock Reality. Shown only when
                          the backend returned a simulation. This is the
                          source of truth for picker decisions: real
                          avg landed cost from picked bales + Uster
                          verdict. The forecast block above stays as a
                          quoting reference. */}
                      {opt.stock_simulation && (
                        <StockRealityPanel sim={opt.stock_simulation} />
                      )}

                      {opt.notes.length > 0 && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ fontSize: 11 }}
                          message={
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {opt.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                          }
                        />
                      )}

                      {/* Sprint 0071 — Use button. The benchmarks are
                          a TARGET, not a wall. We never disable the
                          button on Uster grounds because today's stock
                          may genuinely make on-benchmark impossible —
                          the operator still needs to mix something.
                          Instead the button styling + copy reflect how
                          far from benchmark the variant is, so the
                          user picks with eyes open. INFEASIBLE (literal
                          empty stock) is the one true block. */}
                      {(() => {
                        const sim = opt.stock_simulation
                        const blockedByRecipe = opt.status === 'BLOCKED'
                        const infeasible = sim?.verdict === 'INFEASIBLE'
                        if (blockedByRecipe) return null

                        const verdict = sim?.verdict
                        const offBenchmark = verdict === 'OFF_BENCHMARK' || verdict === 'FAR_OFF'
                        const isSelected = selectedVariant === opt.recipe_variant
                        const reasons = sim?.major_reasons ?? sim?.block_reasons ?? []
                        const tooltip = reasons.length ? reasons.join(' · ') : undefined

                        let label: string
                        if (isSelected) {
                          label = `✓ Selected — ${variantCfg.label}`
                        } else if (infeasible) {
                          label = 'No stock — top up godown'
                        } else if (offBenchmark) {
                          label = `Use anyway (${verdict === 'FAR_OFF' ? 'far off' : 'off'} benchmark)`
                        } else {
                          label = `Use ${variantCfg.label}`
                        }

                        return (
                          <Button
                            block
                            type={isSelected ? 'primary' : (verdict === 'ON_BENCHMARK' ? 'primary' : 'default')}
                            icon={isSelected ? <CheckCircleOutlined /> : undefined}
                            onClick={() => setSelectedVariant(opt.recipe_variant)}
                            disabled={!!infeasible}
                            danger={offBenchmark}
                            title={tooltip}
                          >
                            {label}
                          </Button>
                        )
                      })()}
                    </Space>
                  </Card>
                </Col>
              )
            })}
          </Row>

          {/* Confirmation banner once a selection is made. Shows the
              user what they picked + nudges them to the next step. */}
          {selectedVariant && (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: 16 }}
              message={
                <Space wrap>
                  <Text strong>
                    Selected: {VARIANT_TAG[selectedVariant]?.label} for {countNe}s {yarnType.replace(/_/g, ' ')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Next step: open the linked Sales Order → click "Generate Mix Plan" with variant {selectedVariant}
                  </Text>
                </Space>
              }
            />
          )}

          {data.options.length === 0 && (
            <Empty description={`No recipes seeded for ${countNe}s ${yarnType}`} />
          )}
        </>
      )}
    </App>
  )
}
